import React, { useState } from 'react'
import { Dialog, DialogPanel, DialogBackdrop, DialogTitle, Transition } from '@headlessui/react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faXmark, faSpinner, faXmarkCircle } from '@fortawesome/free-solid-svg-icons'
import axios from 'axios'
import moment from 'moment'
import {toast} from 'sonner'

const InviteMeetingModal = ({ openInvite, setOpenInvite }) => {
  const [meetingName, setMeetingName] = useState('')
  const [meetingLocation, setMeetingLocation] = useState('')
  const [meetingDate, setMeetingDate] = useState('')
  const [meetingAttendees, setMeetingAttendees] = useState('')
  const [meetingPlace, setMeetingPlace] = useState('None Selected')
  const [formSubmitted, setFormSubmitted] = useState(false)
  const [loading, setLoading] = useState(false)
  const [showThanks, setShowThanks] = useState(false)

  const handleClose = () => {
    setShowThanks(false)
    setOpenInvite(false)
  }

  const resetInputs = () => {
    setMeetingName('')
    setMeetingLocation('')
    setMeetingDate('')
    setMeetingPlace('None Selected')
    setMeetingAttendees('')
  }

  const submitForm = async () => {
    setLoading(true)
    setFormSubmitted(true)

    try {
      const params = {
        meeting_name: meetingName,
        meeting_location: meetingLocation,
        meeting_attendees: meetingAttendees,
        meeting_date: moment(meetingDate).format('DD/MM/YYYY HH:mm'),
        meeting_place: meetingPlace,
        school_name: window?.Policy?.orgName || 'Unknown School',
      }

      await axios.post('api/invite/send', params)
      setLoading(false)
      setFormSubmitted(false)
      setShowThanks(true)
      resetInputs()
      toast.success('Meeting invite sent!')
    } catch (error) {
      toast.error(error)
      setLoading(false)
    }
  }

  return (
    <Transition show={openInvite} as={React.Fragment} >
      <Dialog as="div" className="relative z-50" onClose={handleClose}>
        <DialogBackdrop className="fixed inset-0 bg-black bg-opacity-30" />
        <div className="fixed inset-0 flex items-center justify-center p-4">
          <DialogPanel className="w-full max-w-xl rounded-lg shadow-xl bg-white overflow-hidden">
            {/* Header */}
            <div className="flex items-start justify-between mb-4 p-5 border-b border-gray-200">
              <DialogTitle className="text-lg font-semibold text-gray-800 text-center w-full">
                Invite us to your meeting
              </DialogTitle>
              <button className="text-gray-500 hover:text-gray-700" onClick={handleClose}>
                <FontAwesomeIcon icon={faXmarkCircle} className="h-5 w-5" />
              </button>
            </div>

            {/* Body */}
            <div className="p-6 space-y-4">
              {!formSubmitted && !showThanks && (
                <>
                  <p className="text-center text-gray-700">
                    We would love to speak at your meetings! If you can invite us to an upcoming trust, cluster or federation meeting,
                    we’ll sponsor the event up to <strong>£200</strong>!
                  </p>
                  <p className="text-center font-semibold text-gray-800">
                    Please enter the details of the meeting below.
                  </p>

                  <form className="space-y-4" onSubmit={(e) => { e.preventDefault(); submitForm() }}>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Meeting Name</label>
                      <input
                        type="text"
                        className="mt-1 block w-full border border-gray-300 rounded-md p-2"
                        value={meetingName}
                        onChange={(e) => setMeetingName(e.target.value)}
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700">Meeting Location</label>
                      <input
                        type="text"
                        className="mt-1 block w-full border border-gray-300 rounded-md p-2"
                        value={meetingLocation}
                        onChange={(e) => setMeetingLocation(e.target.value)}
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700">Meeting Date</label>
                      <input
                        type="datetime-local"
                        className="mt-1 block w-full border border-gray-300 rounded-md p-2"
                        value={meetingDate}
                        onChange={(e) => setMeetingDate(e.target.value)}
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700">Number of Attendees</label>
                      <input
                        type="number"
                        className="mt-1 block w-full border border-gray-300 rounded-md p-2"
                        value={meetingAttendees}
                        onChange={(e) => setMeetingAttendees(e.target.value)}
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700">Meeting Place (On-site / Digital)</label>
                      <select
                        className="mt-1 block w-full border border-gray-300 rounded-md p-2"
                        value={meetingPlace}
                        onChange={(e) => setMeetingPlace(e.target.value)}
                        required
                      >
                        <option value="None Selected">Select an option</option>
                        <option value="on-site">On-site</option>
                        <option value="digital">Digital</option>
                      </select>
                    </div>

                    <div className="text-center">
                      <button
                        type="submit"
                        className="w-full bg-sas hover:bg-sas-dark text-white font-semibold py-2 px-4 rounded shadow"
                      >
                        Send Invite
                      </button>
                    </div>
                  </form>
                </>
              )}

              {loading && (
                <div className="text-center py-6">
                  <p className="mb-4">Sending Invite...</p>
                  <FontAwesomeIcon icon={faSpinner} spin className="text-blue-600 text-4xl" />
                </div>
              )}

              {!formSubmitted && !loading && showThanks && (
                <div className="text-center py-6">
                  <p className="text-lg font-bold mb-4">Thank you for your invite!</p>
                  <button
                    onClick={handleClose}
                    className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-4 rounded shadow"
                  >
                    Close
                  </button>
                </div>
              )}
            </div>
          </DialogPanel>
        </div>
      </Dialog>
    </Transition>
  )
}

export default InviteMeetingModal